import { z } from "zod";

// Type import.meta.env without depending on vite/client, so downstream
// packages typecheck this file. The literal `import.meta.env` token below is
// what Vite statically replaces with the app's env at build time — do NOT
// alias `import.meta` first or the replacement won't happen.
declare global {
  interface ImportMeta {
    readonly env?: Record<string, string | undefined>;
  }
}

/**
 * Runtime env is validated once, at module load, against this schema.
 * A missing/invalid var fails fast with a readable error instead of
 * surfacing as `undefined` deep inside a request.
 *
 * All client-exposed vars MUST be prefixed `VITE_` (Vite requirement).
 */
const EnvSchema = z.object({
  VITE_APP_NAME: z.string().default("astro-app"),
  VITE_ENV: z.enum(["development", "staging", "production"]).default("development"),
  VITE_API_BASE_URL: z.string().url(),
  VITE_AUTH_BASE_URL: z.string().url(), // the auth BFF, e.g. https://auth.astrorajumaharaj.com
  VITE_COOKIE_DOMAIN: z.string().default(".astrorajumaharaj.com"),
  /** Central login page. Defaults to the website's /login route in production. */
  VITE_LOGIN_URL: z.string().url().optional(),
  /** Socket server origin. Defaults to the origin of VITE_API_BASE_URL. */
  VITE_SOCKET_URL: z.string().url().optional(),
  /** Razorpay public key id (`rzp_...`). Optional — unset in dev until issued. */
  VITE_RAZORPAY_KEY: z.string().optional(),
  /**
   * LiveKit stream server (the astrology-admin-v2 API, e.g.
   * http://localhost:4000/api/v1 or https://api.astrorajumaharaj.com/api/v1). The
   * browser talks to it DIRECTLY — not through the auth BFF proxy — because it
   * is a separate backend with its own auth. Optional: unset simply leaves the
   * LiveKit classroom unable to connect (it says so on screen).
   */
  VITE_LIVEKIT_API_URL: z.string().url().optional(),
});

export type AppEnv = z.infer<typeof EnvSchema>;

function readEnv(): AppEnv {
  // `import.meta.env` (Vite) in the browser; process.env as a Node fallback.
  const raw =
    import.meta.env ??
    (globalThis as { process?: { env: Record<string, string | undefined> } }).process?.env ??
    {};

  const parsed = EnvSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(
      `[@astro/config] Invalid environment:\n${parsed.error.issues
        .map((i) => `  • ${i.path.join(".")}: ${i.message}`)
        .join("\n")}`
    );
  }
  return parsed.data;
}

export const env: AppEnv = readEnv();

/** Production origins — one subdomain per app. */
const PROD_APP_URLS = {
  website: "https://astrorajumaharaj.com",
  jyotish: "https://jyotish.astrorajumaharaj.com",
  shop: "https://shop.astrorajumaharaj.com",
  education: "https://education.astrorajumaharaj.com",
  auth: "https://auth.astrorajumaharaj.com",
} as const;

/**
 * Staging origins. RETIRED as of 2026-10: vedicastral.in was decommissioned when
 * the project moved to astrorajumaharaj.com, so there is no staging domain right
 * now and `VITE_ENV=staging` deliberately resolves to the production origins
 * rather than to dead hosts. Deploys should build with `VITE_ENV=production`.
 * Replace this with its own origin map if a new staging domain is provisioned.
 */
const TEST_APP_URLS = PROD_APP_URLS;

export type AppKey = keyof typeof PROD_APP_URLS;

/** Local dev origins — ports match each app's vite.config.ts. */
const DEV_APP_URLS: Record<AppKey, string> = {
  website: "http://localhost:5173",
  jyotish: "http://localhost:5174",
  shop: "http://localhost:5175",
  education: "http://localhost:5176",
  auth: "http://localhost:4000",
};

/**
 * Central registry of app URLs — the source of truth for cross-app links.
 * Environment-aware: localhost ports in development, subdomains otherwise.
 */
export const APP_URLS: Record<AppKey, string> =
  env.VITE_ENV === "development" ? DEV_APP_URLS : env.VITE_ENV === "staging" ? TEST_APP_URLS : PROD_APP_URLS;

/**
 * Absolute URL into a sibling app, correct for the current environment.
 * Cross-app navigation is a full-page load (each app is its own SPA), so
 * use this in a plain <a href> or window.location.assign().
 */
export function appUrl(app: AppKey, path = ""): string {
  const base = APP_URLS[app];
  // JS callers get no AppKey type-checking — fail loudly, not with a dead link.
  if (!base) {
    console.error(`[@astro/config] appUrl: unknown app key "${app}"`);
    return "#";
  }
  if (!path) return base;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}
